/**
 * What the catalog says about whether a home can still be bought — W16.
 *
 * **Every listing carries a `status`, and until now only two places read it.**
 * `/api/v1/search` indexes `status = active` only, so on the results grid the
 * field is a constant and its absence never showed. Everywhere else it is not a
 * constant: favourites, the recently-viewed rail, a compare link, a saved-search
 * e-mail and any shared URL all fetch a home **by id**, and `GET /listings/{id}`
 * and `/listings/batch` return the row whatever its status (`publicVisible` in
 * the repository filters `internet_list` and `display_restricted` — not status).
 * So a home that expired, went pending or sold since the buyer saved it kept
 * rendering as available, with a live "Request a tour" button on it.
 *
 * Not hypothetical: the ingestion pipeline expires anything the MLS feed stops
 * confirming (`ExpireListings` sets `status = 'expired'` + `status_change_date`),
 * and the roll-call drain is clearing years of stale rows — roughly a third of
 * the corpus. "The home went off the market between the search and the return"
 * is the common case, not the edge one.
 *
 * The vocabulary is the backend's own (`pkg/canonical/listing.go`): `active`,
 * `active_contingent`, `pending`, `sold`, `expired`, `cancelled`, `withdrawn`.
 * An unrecognised status is carried through verbatim rather than assumed active —
 * that assumption is the one that can put a buyer on a home that isn't there.
 *
 * This is a deliberate port of the iOS `ListingAvailability` (GRCore), including
 * its copy, so the two clients cannot drift into saying different things about
 * the same home.
 */

export type Availability =
  | { kind: 'active' }
  /** `active_contingent` — under contract, still taking backups. */
  | { kind: 'contingent' }
  | { kind: 'pending' }
  | { kind: 'sold' }
  /** `expired` / `cancelled` / `withdrawn`: off the market, and the feed does not
   *  say whether it sold. Carries the raw status so copy can stay exact. */
  | { kind: 'off-market'; raw: string }
  /** The catalog no longer returns this id at all — a 404 or an omission from a
   *  batch response. Stronger than any status: the row is gone. */
  | { kind: 'missing' }
  /** Something the feed sent that this app has never seen. Shown as-is. */
  | { kind: 'unknown'; raw: string };

/** The catalog has lost this home entirely (404 / omitted from a batch). */
export const MISSING: Availability = { kind: 'missing' };

/**
 * Parse a catalog status.
 *
 * An **empty or absent** status resolves to `active`, matching iOS: the light
 * map projection and some cached snapshots omit the field, and treating "not
 * told" as "off the market" would badge the whole map.
 */
export function availabilityOf(status: string | null | undefined): Availability {
  // COMPACT-DECODED feed values arrive as human text ("Active Contingent"), the
  // database's as snake_case; normalise both.
  const key = (status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  switch (key) {
    case '':
    case 'active':
      return { kind: 'active' };
    case 'active_contingent':
    case 'contingent':
    case 'active_with_contingency':
      return { kind: 'contingent' };
    case 'pending':
    case 'active_under_contract':
      return { kind: 'pending' };
    case 'sold':
    case 'closed':
      return { kind: 'sold' };
    case 'expired':
    case 'cancelled':
    case 'canceled':
    case 'withdrawn':
    case 'off_market':
      return { kind: 'off-market', raw: key };
    default:
      return { kind: 'unknown', raw: status! };
  }
}

/**
 * Whether an offer can still realistically be made — i.e. whether anything on
 * the page may offer to book a viewing. Contingent listings are still taking
 * backups, so they stay buyable; pending ones do not.
 */
export function isBuyable(a: Availability): boolean {
  return a.kind === 'active' || a.kind === 'contingent';
}

/**
 * True when the home is gone from the market entirely (as opposed to under
 * contract). Drives the "this may have vanished since you saved it" copy and
 * the alarm colour.
 */
export function isGone(a: Availability): boolean {
  return a.kind === 'sold' || a.kind === 'off-market' || a.kind === 'missing';
}

/**
 * Short badge for a card. `null` for an active listing — a badge reading "for
 * sale" on a for-sale home is noise, and every card in the results grid is
 * active.
 */
export function badgeLabel(a: Availability): string | null {
  switch (a.kind) {
    case 'active':
      return null;
    case 'contingent':
      return 'Accepting backups';
    case 'pending':
      return 'Sale pending';
    case 'sold':
      return 'Sold';
    case 'off-market':
      return 'No longer listed';
    case 'missing':
      return 'No longer available';
    case 'unknown':
      return capitalise(a.raw);
  }
}

/** The banner headline on a detail page. */
export function bannerHeadline(a: Availability): string | null {
  switch (a.kind) {
    case 'active':
      return null;
    case 'contingent':
      return 'Under contract, accepting backups';
    case 'pending':
      return 'Sale pending';
    case 'sold':
      return 'This home has sold';
    case 'off-market':
      return 'No longer listed';
    case 'missing':
      return 'No longer available';
    case 'unknown':
      return `Listing status: ${capitalise(a.raw)}`;
  }
}

/**
 * What we actually know, and nothing more.
 *
 * `statusChangeDate` for an expiry is the moment **our own pipeline** stopped
 * seeing the home in the MLS feed, which is exactly what the copy claims — not
 * a sale date, which the feed does not give us for an expiry.
 */
export function bannerExplanation(
  a: Availability,
  statusChangeDate?: string | null
): string | null {
  const day = formatDayUTC(statusChangeDate);
  const when = day ? ` on ${day}` : '';
  switch (a.kind) {
    case 'active':
      return null;
    case 'contingent':
      return 'The seller has accepted an offer but is still taking backup offers.';
    case 'pending':
      return `The seller has accepted an offer${when}. Pending sales sometimes fall through — your advisor can ask where this one stands.`;
    case 'sold':
      return `It is no longer for sale${when}.`;
    case 'off-market':
      return `We last saw this home in the MLS feed${when}. It may have sold, or the seller may have taken it off the market — the feed doesn't say which.`;
    case 'missing':
      return 'This home has been removed from the MLS catalog. Your advisor can find out what happened to it.';
    case 'unknown':
      return "The MLS sent a status we don't recognise, so we're showing it as-is rather than guessing.";
  }
}

/**
 * Badge / banner colours.
 *
 * Solid, not tinted: a card badge sits **over the listing photo**, where the
 * paper-canvas tints (`bg-coral-500/15 text-coral-300`) are effectively
 * transparent and the dark-red text lands on whatever the photo happens to be.
 * Gone reads as an alarm; under-contract reads as information.
 */
export function badgeClasses(a: Availability): string {
  return isGone(a) ? 'bg-coral-500 text-white' : 'bg-ink text-canvas';
}

/** Panel colours for the detail-page banner (on paper, so tints are correct). */
export function bannerClasses(a: Availability): string {
  return isGone(a)
    ? 'border-coral-500/30 bg-coral-500/10'
    : 'border-gold-500/30 bg-gold-500/10';
}

function capitalise(raw: string): string {
  const words = raw.replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : words;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * "Jul 20" from an RFC3339 timestamp, read as the calendar day the API sent.
 *
 * Deliberately **not** `toLocaleDateString`: this string is rendered on the
 * server (the detail page is ISR-cached) and again on the client during
 * hydration, and those two run in different timezones — a UTC-morning timestamp
 * is the previous day in California, which is a React #418 hydration mismatch on
 * a page whose whole job is to be trustworthy. Reading the date part of the
 * string cannot disagree with itself.
 */
export function formatDayUTC(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${MONTHS[month - 1]} ${day}`;
}

/**
 * One rail-width line about a home that can no longer be bought.
 *
 * The card badge beside it already says *what* — this adds the only thing we know
 * that it doesn't: **when our pipeline recorded it**. Without a date it degrades
 * to the badge rather than inventing a "recently".
 */
export function newsLine(a: Availability, statusChangeDate?: string | null): string {
  const label = badgeLabel(a) ?? 'No longer available';
  const day = formatDayUTC(statusChangeDate);
  if (!day) return label;
  switch (a.kind) {
    case 'off-market':
      return `${label} — last confirmed by the MLS on ${day}`;
    case 'sold':
      return `${label} — recorded on ${day}`;
    case 'pending':
    case 'contingent':
      return `${label} — since ${day}`;
    default:
      return label;
  }
}
